import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../../ui';
import { Ministry } from '../../../models/ministry/ministry';
import { School } from '../../../models/school/school';
import { ministryService } from '../../../services/ministry-service';
import { schoolService } from '../../../services/school-service';
import { Language } from '../../../constants/language';
import { usePageSEO } from '../../../hooks';

const About: React.FC = () => {
  // SEO - Updates title, description, and meta tags for the about page
  usePageSEO('about');

  const { t, i18n } = useTranslation();
  
  // State for ministries and schools
  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch ministries and schools data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const currentLanguage = i18n.language as Language;
        
        // Fetch all ministries and schools
        const [ministriesResult, schoolsResult] = await Promise.all([
          ministryService.getMinistries(currentLanguage, undefined, undefined, 1, 20),
          schoolService.getSchools(currentLanguage, undefined, undefined, 1, 20)
        ]);
        
        setMinistries(ministriesResult.data);
        setSchools(schoolsResult.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [i18n.language]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.1)_50%,transparent_75%)]"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="inline-block mb-8">
            <div className="relative w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-white to-gray-100 rounded-full flex items-center justify-center shadow-2xl mx-auto border-4 border-white/20 backdrop-blur-sm">
              <span className="text-black text-4xl md:text-5xl font-bold">ZOE</span>
              <div className="absolute inset-0 rounded-full bg-white/10 animate-pulse"></div>
              <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-spin-slow"></div>
            </div>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
            {t('pages.about.title')}
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12">
            {t('pages.about.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/ministries">
              <Button 
                size="lg"
                className="bg-white text-black hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                {t('pages.about.hero.exploreMinistries')}
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                {t('pages.about.hero.getInTouch')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-1/4 left-10 w-32 h-32 border border-white/10 rotate-45 animate-spin-slow"></div>
        <div className="absolute bottom-1/4 right-10 w-24 h-24 border border-white/15 rotate-12 animate-pulse"></div>
      </section>

      {/* Who We Are Section - White Background */}
      <section className="bg-white text-black py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">{t('pages.about.whoWeAre.title')}</h2>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800">
                  {t('pages.about.whatIsZoe')}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  {t('pages.about.description')}
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {t('pages.about.mission')}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg border border-gray-100">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-black text-white rounded-xl flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                </div>
                <h4 className="text-xl font-bold mb-4 text-gray-800 text-center">{t('pages.about.vision.title')}</h4>
                <p className="text-gray-600 mb-4 leading-relaxed text-center">{t('pages.about.vision.content')}</p>
                <blockquote className="border-l-4 border-black pl-4 py-2 mb-4">
                  <p className="text-gray-700 font-medium italic">"{t('pages.about.vision.quote')}"</p>
                </blockquote>
                <p className="text-gray-600 text-sm leading-relaxed text-center">{t('pages.about.vision.mission')}</p>
              </div>
            </div>

            {/* Values Grid */}
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h4 className="text-lg font-bold mb-3">{t('pages.about.values.teaching.title')}</h4>
                <p className="text-gray-600 text-sm">{t('pages.about.values.teaching.description')}</p>
              </div>

              <div className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h4 className="text-lg font-bold mb-3">{t('pages.about.values.community.title')}</h4>
                <p className="text-gray-600 text-sm">{t('pages.about.values.community.description')}</p>
              </div>

              <div className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h4 className="text-lg font-bold mb-3">{t('pages.about.values.service.title')}</h4>
                <p className="text-gray-600 text-sm">{t('pages.about.values.service.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Ministry Overview Section */}
      <section className="bg-white text-black py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8">{t('pages.about.ministries.title')}</h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {t('pages.about.ministries.description')}
            </p>
          </div>

          {/* Ministry Sectors Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }, (_, index) => (
                <div key={index} className="p-8 border border-gray-200 rounded-xl animate-pulse">
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {ministries.map((ministry) => (
                <div
                  key={ministry.id}
                  className="group p-8 border border-gray-200 rounded-xl hover:border-black hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <div className="flex items-center justify-center w-16 h-16 bg-black text-white rounded-full mb-6 group-hover:bg-gray-800 transition-colors">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold mb-4 group-hover:text-black transition-colors">{ministry.title}</h3>
                  {ministry.duration && (
                    <span className="inline-block bg-black text-white px-3 py-1 rounded-full text-sm mb-4 group-hover:bg-gray-800 transition-colors">
                      {ministry.duration}
                    </span>
                  )}
                  <p className="text-gray-600 leading-relaxed mb-6">{ministry.description}</p>
                  <div className="mt-auto">
                    <Link 
                      to={`/ministries/${ministry.key}`}
                      className="inline-flex items-center text-black font-medium hover:text-gray-700 transition-colors"
                    >
                      {t('general.actions.learnMore')}
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Schools Section - Black Background */}
      <section className="bg-black text-white py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8">{t('pages.schools.heroTitle')}</h2>
            <p className="text-lg md:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              {t('pages.schools.heroDescription')}
            </p>
          </div>

          {/* Schools Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }, (_, index) => (
                <div key={index} className="p-8 bg-gray-800 rounded-xl animate-pulse">
                  <div className="h-6 bg-gray-700 rounded mb-4"></div>
                  <div className="h-4 bg-gray-700 rounded w-24 mb-4"></div>
                  <div className="h-4 bg-gray-700 rounded mb-2"></div>
                  <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {schools.map((school) => (
                <div
                  key={school.id}
                  className="group p-8 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl border border-gray-700 hover:border-gray-600 transition-all duration-300 hover:scale-105"
                >
                  <div className="flex items-center justify-center w-16 h-16 bg-white text-black rounded-full mb-6 group-hover:bg-gray-100 transition-colors">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-white group-hover:text-gray-100 transition-colors">{school.title}</h3>
                  {school.duration && (
                    <span className="inline-block bg-white text-black px-3 py-1 rounded-full text-sm mb-4 group-hover:bg-gray-100 transition-colors">
                      {school.duration}
                    </span>
                  )}
                  <p className="text-gray-300 leading-relaxed mb-6">{school.description}</p>
                  <div className="flex justify-between items-center mt-auto">
                    <Link 
                      to={`/schools/${school.key}/${school.id}`}
                      className="inline-flex items-center text-white font-medium hover:text-gray-300 transition-colors"
                    >
                      {t('general.actions.learnMore')}
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                    {school.tuition && (
                      <span className="text-sm text-gray-400">
                        {school.tuition.amount === 0 ? t('general.badges.free') : `${school.tuition.amount} ${school.tuition.currency}`}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/schools">
              <Button 
                size="lg"
                className="bg-white text-black hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                {t('pages.schools.exploreSchools')}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Core Values Section - White Background */}
      <section className="bg-white text-black py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">{t('pages.about.values.title')}</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {t('pages.about.values.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(t('pages.about.values.list', { returnObjects: true }) as string[]).map((value, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border border-gray-200 hover:border-black hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-black text-white rounded-full mb-6 mx-auto">
                  <span className="text-2xl font-bold">{index + 1}</span>
                </div>
                <h3 className="text-xl font-bold text-center mb-4 text-black">{value}</h3>
                <div className="w-12 h-1 bg-black rounded mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer Message Section */}
      <section className="bg-white text-black py-20 lg:py-32">
        <div className="max-w-5xl mx-auto text-center px-6">
          <div className="mb-12">
            <div className="w-20 h-20 bg-gradient-to-br from-black to-gray-800 rounded-full flex items-center justify-center shadow-xl mx-auto mb-8">
              <span className="text-white text-2xl font-bold">Z</span>
            </div>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
              {t('footer.description')}
            </p>
            <p className="text-lg md:text-xl text-gray-600 font-medium">
              {t('footer.tagline')}
            </p>
          </div>
        </div>
      </section>

      {/* The Meaning of 'ZOE' Section - Black Background */}
      <section className="bg-black text-white py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="mb-12">
            <div className="w-24 h-24 bg-gradient-to-br from-white to-gray-200 rounded-full flex items-center justify-center shadow-2xl mx-auto mb-8">
              <span className="text-black text-3xl font-bold">ZOE</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-8">{t('pages.about.zoe.title')}</h2>
            <div className="w-20 h-1 bg-white mx-auto mb-8"></div>
          </div>
          
          <div className="prose prose-xl prose-invert max-w-none">
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              {t('pages.about.zoe.greekMeaning')}
            </p>
            
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12 mb-8">
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
                {t('pages.about.zoe.biblicalFoundation')}
              </p>
              <blockquote className="text-2xl md:text-3xl font-bold text-white italic border-l-4 border-white pl-6">
                "{t('pages.about.zoe.verse')}"
              </blockquote>
              <cite className="text-gray-400 text-base mt-4 block">
                {t('pages.about.zoe.reference')}
              </cite>
            </div>

            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-8">
              {t('pages.about.zoe.ourMission')}
            </p>

            <div className="grid md:grid-cols-3 gap-8 mt-12">
              {(t('pages.about.zoe.principles', { returnObjects: true }) as Array<{title: string, description: string}>).map((principle, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-black text-2xl font-bold">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{principle.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{principle.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section - White Background */}
      <section className="bg-white text-black py-20 lg:py-32">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">{t('pages.about.cta.title')}</h2>
          <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed">
            {t('pages.about.cta.description')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to="/events" 
              className="bg-black text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-800 transition-colors duration-200 inline-flex items-center justify-center"
            >
              {t('pages.about.cta.joinEvents')}
            </Link>
            <Link 
              to="/ministries" 
              className="border-2 border-black text-black px-8 py-4 rounded-full font-semibold text-lg hover:bg-black hover:text-white transition-colors duration-200 inline-flex items-center justify-center"
            >
              {t('pages.about.cta.exploreMinistries')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;